import { useUserStore } from '@/store/useUserStore';
import { apiCache } from './cacheCleanup';
import { ApiError } from './apiError';
import {
  formDataToObject,
  headersToObject,
  isApiLoggingEnabled,
  logApiCall,
  redactSensitive,
} from './apiLogger';
import { isBugReportEnabled, pushBugReportApiLog } from './bugReportBuffer';
import { getTimeOverrideOffsetSeconds, isTimeOverrideEnabled } from './timeOverride';

const BASE_URL = import.meta.env.VITE_API_URL;

interface APIConfig extends RequestInit {
  body?: any;
  skipCache?: boolean; // Add option to skip cache
  cacheTTL?: number; // Add custom TTL
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getResourceRootPath = (endpoint: string): string | null => {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const pathWithoutQuery = normalizedEndpoint.split('?')[0];
  const segments = pathWithoutQuery.split('/').filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  return `/${segments[0]}`;
};

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function request<T>(endpoint: string, { body, skipCache = false, cacheTTL = CACHE_TTL, ...customConfig }: APIConfig = {}): Promise<T> {
  const token = useUserStore.getState().token;
  const user = useUserStore.getState().user;
  const isSuperAdmin = useUserStore.getState().isSuperAdmin;
  let selectedWorkspaceId = useUserStore.getState().selectedWorkspaceId;

  // Belt-and-suspenders: if a super admin is firing a workspace-scoped request
  // but the workspace list hasn't bootstrapped yet (e.g. after a hard reload,
  // or a hook fired before the gate finished), block on the bootstrap so we
  // don't silently drop the X-Workspace-Id header. The /workspaces endpoint
  // itself is exempt below via `noWorkspaceHeaderEndpoints`.
  if (isSuperAdmin && !selectedWorkspaceId) {
    const noWsForBootstrap = ['/workspaces', '/sbl-khas', '/auth/login', '/auth/register'];
    const needsWorkspaceHeader = !noWsForBootstrap.some((ep) => endpoint.includes(ep));
    if (needsWorkspaceHeader) {
      await useUserStore.getState().ensureWorkspaceReady();
      selectedWorkspaceId = useUserStore.getState().selectedWorkspaceId;
    }
  }
  
  // Generate cache key including user context
  const cacheKey = `${endpoint}_${token || 'no-token'}_${selectedWorkspaceId || 'no-ws'}`;
  const isGetRequest = customConfig.method === undefined || customConfig.method === 'GET';
  
  // Check cache for GET requests
  if (!skipCache && isGetRequest) {
    const cached = apiCache.get(cacheKey);
    if (cached) {
      console.log('[API] Using cached data for:', endpoint);
      return cached as T;
    }
  }
  
  const finalEndpoint = endpoint;

  // Endpoints that don't require X-Workspace-Id header (super admin workspace management)
  const noWorkspaceHeaderEndpoints = ['/workspaces', '/sbl-khas', '/auth/login', '/auth/register'];
  const requiresWorkspaceHeader = !noWorkspaceHeaderEndpoints.some(ep => finalEndpoint.includes(ep));
  
  // Check if body is FormData
  const isFormData = body instanceof FormData;
  
  const headers: HeadersInit = {};
  
  // Always request JSON responses to ensure API returns JSON errors for validation
  (headers as Record<string, string>)['Accept'] = 'application/json';

  // Send client timezone so backend can normalize timezone-naive datetime inputs
  (headers as Record<string, string>)['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // QA testers can shift the backend clock via a local panel (non-prod only).
  if (isTimeOverrideEnabled()) {
    const offset = getTimeOverrideOffsetSeconds();
    if (offset !== 0) {
      (headers as Record<string, string>)['X-Time-Offset'] = String(offset);
    }
  }

  // Only set Content-Type for non-FormData requests
  // Browser will set the correct Content-Type with boundary for FormData
  if (!isFormData) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  // Add X-Workspace-Id header (skip for workspace management endpoints)
  // For super admins: use selectedWorkspaceId if set
  // For regular users: use their workspace_id from auth data
  if (requiresWorkspaceHeader) {
    if (isSuperAdmin) {
      if (selectedWorkspaceId) {
        (headers as Record<string, string>)['X-Workspace-Id'] = selectedWorkspaceId.toString();
        console.log('[API] Adding X-Workspace-Id for super_admin:', selectedWorkspaceId);
      } else {
        console.log('[API] Super admin with no workspace selected - X-Workspace-Id header omitted');
      }
    } else if (user?.workspace_id) {
      (headers as Record<string, string>)['X-Workspace-Id'] = user.workspace_id.toString();
      console.log('[API] Adding X-Workspace-Id for regular user:', user.workspace_id);
    } else {
      console.log('[API] No workspace ID available');
    }
  } else {
    console.log('[API] Skipping X-Workspace-Id header for endpoint:', endpoint);
  }

  const config: RequestInit = {
    ...customConfig,
    headers: { ...headers, ...customConfig.headers },
  };

  if (body) {
    // For FormData, send as-is; for others, stringify
    config.body = isFormData ? body : JSON.stringify(body);
  }

  const finalUrl = finalEndpoint.startsWith('/') ? finalEndpoint : `/${finalEndpoint}`;

  const logging = isApiLoggingEnabled() || isBugReportEnabled();
  const requestMethod = (customConfig.method ?? 'GET').toUpperCase();
  const requestStartedAt = Date.now();
  const requestTimestamp = new Date(requestStartedAt).toISOString();
  let loggableRequestBody: unknown;
  if (logging && body !== undefined) {
    loggableRequestBody = isFormData ? formDataToObject(body as FormData) : body;
  }
  const loggableEndpoint = endpoint.split('?')[0];
  const fullUrl = `${BASE_URL}${finalUrl}`;
  const loggableUrl = fullUrl.replace(/([?&])_t=\d+(&|$)/, (_m, p1, p2) => (p2 === '&' ? p1 : ''));
  let responseLogged = false;

  try {
    // 1. Call API
    const response = await fetch(fullUrl, config);

    // 2. Try to parse JSON immediately (Success OR Error)
    const data = await response.json().catch(() => null);

    if (logging) {
      const payload = {
        method: requestMethod,
        url: loggableUrl,
        endpoint: loggableEndpoint,
        status: response.status,
        durationMs: Date.now() - requestStartedAt,
        timestamp: requestTimestamp,
        requestHeaders: headersToObject(config.headers),
        responseHeaders: headersToObject(response.headers),
        // Redacted here rather than at the sinks so the dev logger and the bug
        // reporter can never disagree about what is safe to keep.
        requestBody: redactSensitive(loggableRequestBody),
        responseBody: redactSensitive(data),
      };
      logApiCall(payload);
      pushBugReportApiLog(payload);
      responseLogged = true;
    }

    // 3. Handle Global 401 (Logout) Side Effect
    // We still want to log them out, but we ALSO return the data so the UI knows why.
    if (response.status === 401) {
      useUserStore.getState().logout();
    }

    // 4. Handle JSON response for non-OK statuses (validation, etc.)
    if (!response.ok) {
      // Prefer explicit message from API
      const errorMessage = (data && (data.message || (data.errors ? Object.values(data.errors).flat().join(' ') : null))) || response.statusText || 'API Error';
      // ApiError keeps the status and the server's error_code alongside the
      // message, so a caller can tell one refusal from another.
      throw new ApiError(errorMessage, response.status, data);
    }

    // 5. Normal successful JSON response
    if (data) {
      // Backwards-compat: backend sometimes uses { success:false, message } pattern
      if (data.success === false) {
        throw new ApiError(data.message || "API Error", response.status, data);
      }
        
      // Cache successful GET responses
      if (!skipCache && isGetRequest) {
        apiCache.set(cacheKey, data, cacheTTL);
      } else if (!isGetRequest) {
        const resourceRootPath = getResourceRootPath(endpoint);

        if (resourceRootPath) {
          apiCache.clearByPattern(new RegExp(`^${escapeRegExp(resourceRootPath)}`));
        } else {
          apiCache.clear();
        }
      }

      return data as T;
    }

    // 6. Fallback: If status is OK but no JSON was returned
    return {} as T; // Should not happen if backend is consistent
  } catch (error) {
    if (logging && !responseLogged) {
      const payload = {
        method: requestMethod,
        url: loggableUrl,
        endpoint: loggableEndpoint,
        durationMs: Date.now() - requestStartedAt,
        error: error instanceof Error ? error.message : String(error),
        timestamp: requestTimestamp,
        requestHeaders: headersToObject(config.headers),
        requestBody: redactSensitive(loggableRequestBody),
      };
      logApiCall(payload);
      pushBugReportApiLog(payload);
    }
    // Clear cache on error to prevent stale data
    if (!skipCache && isGetRequest) {
      apiCache.clearByPattern(new RegExp(endpoint));
    }
    throw error;
  }
}

// Export api object with methods
export const api = {
  get: <T>(url: string, config?: APIConfig) => request<T>(url, { method: 'GET', ...config }),
  post: <T>(url: string, body?: any, config?: APIConfig) => 
    request<T>(url, { method: 'POST', body, skipCache: true, ...config }),
  put: <T>(url: string, body?: any, config?: APIConfig) => 
    request<T>(url, { method: 'PUT', body, skipCache: true, ...config }),
  patch: <T>(url: string, body?: any, config?: APIConfig) => 
    request<T>(url, { method: 'PATCH', body, skipCache: true, ...config }),
  delete: <T>(url: string, config?: APIConfig) => 
    request<T>(url, { method: 'DELETE', skipCache: true, ...config }),
  clearCache: () => apiCache.clear(),
};

// Legacy exports for backward compatibility
export const fetcher = <T>(url: string, config?: APIConfig) => api.get<T>(url, config);
export const postData = <T>(url: string, body: any, config?: APIConfig) => api.post<T>(url, body, config);
export const updateData = <T>(url: string, body: any, config?: APIConfig) => api.put<T>(url, body, config);
export const deleteData = <T>(url: string, config?: APIConfig) => api.delete<T>(url, config);